import { Component, computed, effect, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Connection } from '@interfaces/user/connection';
import { User } from '@interfaces/user/user';
import { FocusTrapModule } from 'primeng/focustrap';
import { Listbox, ListboxModule } from 'primeng/listbox';
import { SkeletonModule } from 'primeng/skeleton';
import { combineLatest, delay, map, of, switchMap } from 'rxjs';

import { ContentService } from '@services/content.service';
import { CustomerService } from '@services/customer.service';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';

import { ShellService } from '../../shell.service';

@Component({
  selector: 'bk-account-menu',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, ListboxModule, SkeletonModule, FocusTrapModule],
  templateUrl: './account-menu.component.html',
  styleUrl: './account-menu.component.scss',
})
export class AccountMenuComponent {
  customerService = inject(CustomerService);
  userService = inject(UserService);
  shellService = inject(ShellService);
  navService = inject(NavService);
  texts = inject(ContentService).texts.common;

  user = toSignal(this.userService.user$);
  userLoaded = toSignal(this.userService.userLoaded$);
  selectedConnection = toSignal(this.shellService.selectedConnection$);
  customerId = toSignal(this.shellService.customerId$);
  isAdmin = toSignal(this.userService.isAdmin$);
  isCustomerService = toSignal(this.userService.isCustomerService$);

  listbox = viewChild<Listbox>('listbox');

  formGroup = new FormGroup({
    connection: new FormControl<Connection | null>(null),
  });
  formValue = toSignal(this.formGroup.valueChanges);

  isAdmin$ = this.userService.isAdmin$;
  isCustomerService$ = this.userService.isCustomerService$;
  user$ = this.userService.user$;
  userLoaded$ = this.userService.userLoaded$;

  constructor() {
    effect(() => {
      const selectedConnection = this.selectedConnection();
      this.formGroup.patchValue({ connection: selectedConnection });
    });

    effect(() => {
      const fromValue = this.formValue();
      const user = this.user();
      const selectedConnection = this.selectedConnection();
      if (!fromValue || !fromValue.connection || !user) return;
      if (fromValue.connection.customerId == selectedConnection?.customerId) return;
      this.selectConnection(fromValue.connection.customerId, user);
    });

    effect(() => {
      const listbox = this.listbox();
      if (!listbox || !listbox?.el || !listbox?.el.nativeElement) return;
      const inputElement = this.listbox()?.el.nativeElement.querySelector('input');
      inputElement?.focus();
    });
  }

  adminConnections$ = this.user$.pipe(
    delay(1), // Without this delay, the component will not render connections after login
    switchMap((user) => {
      if (user?.isAdmin || user?.isCustomerService) {
        return this.customerService.getCustomers().pipe(
          map((customers) => {
            const crmConnections = customers.map((customer) => {
              const relations = (customer.erpRelationships || [])
                .filter((relation) => relation.isActiveInErp)
                .map((relation) => relation.erpCustomerId)
                .join(', ');

              return {
                customerId: customer.id,
                name: `${customer.name} (${relations})`,
                permissions: [],
              } as Connection;
            });
            return crmConnections;
          }),
        );
      }
      return of([]);
    }),
  );

  userConnections$ = this.userService.connections$;

  connections$ = combineLatest([this.userConnections$, this.adminConnections$]).pipe(
    map(([userConnections, adminConnections]) => {
      if (adminConnections.length > 0) return [...adminConnections, ...userConnections];
      return userConnections;
    }),
  );

  connections = toSignal(this.connections$);

  connectionsLoaded = computed(() => {
    if (this.isAdmin() || this.isCustomerService()) {
      const connections = this.connections();
      return connections && connections.length > 0;
    }
    return this.userLoaded();
  });

  selectConnection(customerId: string, user: User) {
    this.shellService.selectCustomer(user, customerId);
  }
}
